import { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { Typography, Card, Button, Space, message, Spin } from 'antd';
import { ArrowLeftOutlined } from '@ant-design/icons';
import COGSRuleForm from '../components/COGSRuleForm';
import { cogsService } from '../services/cogsService';

const { Title, Text } = Typography;

const COGSEditPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  // Determine mode from the URL path
  const mode = location.pathname.includes('edit-as-new') ? 'edit-as-new' : 'edit';

  // Get return path from navigation state (preserves filters/pagination)
  const returnPath = location.state?.returnPath || '';
  const backUrl = `/costing-rules${returnPath}`;

  const [rule, setRule] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const fetchRule = async () => {
      try {
        setLoading(true);
        const ruleData = await cogsService.getRule(id);
        setRule(ruleData);
      } catch (error) {
        message.error('Failed to load costing rule');
        navigate(backUrl);
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      fetchRule();
    }
  }, [id, navigate, backUrl]);

  const handleSave = async (formData, options = {}) => {
    const { skipApiCall = false } = options;
    
    setSaving(true);
    try {
      if (!skipApiCall) {
        if (mode === 'edit-as-new') {
          await cogsService.createNewVersionFromExisting(id, formData);
          message.success('New rule version created and submitted for approval');
        } else if (mode === 'edit') {
          await cogsService.updateRule(id, formData);
          message.success('COGS rule updated successfully');
        }
      }
      
      navigate(backUrl);
    } catch (error) {
      message.error('Failed to save costing rule');
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    navigate(backUrl);
  };

  const getPageTitle = () => {
    if (mode === 'edit-as-new') {
      return 'Create New Version of COGS Rule';
    }
    return 'Edit COGS Rule';
  };

  const getPageDescription = () => {
    if (mode === 'edit-as-new') {
      return `Creating a new version of COGS Rule ID: ${id}. This will create a new rule that goes through the approval process.`;
    }
    return `Editing COGS Rule ID: ${id}`;
  };

  if (loading) {
    return (
      <div style={{ padding: '24px', textAlign: 'center' }}>
        <Spin size="large" />
        <div style={{ marginTop: '16px' }}>
          <Text>Loading costing rule...</Text>
        </div>
      </div>
    );
  }

  if (!rule) {
    return (
      <div style={{ padding: '24px' }}>
        <Card>
          <Title level={2}>Rule Not Found</Title>
          <Text>The requested costing rule could not be found.</Text>
          <div style={{ marginTop: '16px' }}>
            <Button onClick={() => navigate(backUrl)}>
              Back to Rules
            </Button>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div>
      <div style={{ marginBottom: '24px' }}>
        <Space>
          <Button
            icon={<ArrowLeftOutlined />}
            onClick={() => navigate(backUrl)}
          >
            Back to Rules
          </Button>
        </Space>
      </div>

      <div style={{ marginBottom: '24px' }}>
        <Title level={2}>{getPageTitle()}</Title>
        <Text type="secondary">
          {getPageDescription()}
        </Text>
      </div>

      <COGSRuleForm
        initialData={rule}
        mode={mode === 'edit-as-new' ? 'edit-as-new' : 'edit'}
        onSave={handleSave}
        onCancel={handleCancel}
        loading={saving}
      />
    </div>
  );
};

export default COGSEditPage;
